import { useCloseRequest } from 'src/modules/shared/hooks/use-close-request';
import { CloseRequestContext } from 'src/modules/shared/contexts/close-request-context';
import type { PopoverProps as MaterialPopoverProps } from '@material-tailwind/react';
import {
  Popover as MaterialPopover,
  PopoverContent,
  PopoverHandler,
} from '@material-tailwind/react';
import React from 'react';
import type { PopoverProps } from 'src/modules/shared/types/popover.type';

const dismissConfig: MaterialPopoverProps['dismiss'] = {
  enabled: true,
  escapeKey: true,
  referencePress: false,
  outsidePress: true,
  ancestorScroll: false,
};

/**
 * Renders shared anchored content with outside and Escape dismissal.
 * @param props - Trigger, content, controlled visibility, and optional labeling.
 * @returns The trigger and portaled popover content.
 */
export const Popover: React.FC<PopoverProps> = ({
  handler,
  nativeHandlerRef,
  children,
  className = '',
  contentClassName = '',
  contentStyle,
  contentId,
  contentAriaLabel,
  placement = 'bottom-end',
  isLoading = false,
  disabled = false,
  open,
  onOpenChange,
}) => {
  const handlerClasses = `inline-block ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`;
  const contentClasses = `p-0 shadow-xl z-[99] ${contentClassName}`;

  const handleOpen = (open: boolean) => {
    if (isLoading || disabled) return;

    onOpenChange(open);
  };

  const closeLayerId = useCloseRequest({
    isOpen: open,
    onClose: () => handleOpen(false),
  });

  return (
    <CloseRequestContext.Provider value={closeLayerId}>
      <MaterialPopover
        open={open}
        handler={handleOpen}
        placement={placement}
        dismiss={dismissConfig}
      >
        <PopoverHandler
          ref={nativeHandlerRef}
          {...(contentId !== undefined && open
            ? { 'aria-controls': contentId }
            : {})}
        >
          {nativeHandlerRef ? (
            handler
          ) : (
            <div className={handlerClasses}>{handler}</div>
          )}
        </PopoverHandler>
        <PopoverContent
          {...(contentId !== undefined ? { id: contentId } : {})}
          {...(contentAriaLabel !== undefined
            ? { 'aria-label': contentAriaLabel }
            : {})}
          className={contentClasses}
          style={contentStyle}
        >
          {children}
        </PopoverContent>
      </MaterialPopover>
    </CloseRequestContext.Provider>
  );
};
